import { Plus } from 'lucide-react';
import type { ChangeEvent } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { env } from '@/constants/env';
import { useModal } from '../../../../components/modals/new/use-modal';
import { MediaLibraryModal } from '../../../image-rendering/components/media-library-modal';
import type { ImageRef } from '../../atoms/state';
import { IMAGE_TO_VIDEO_MAX_IMAGES } from '../../constants/options';

type ChosenImagesStripProps = {
  images: ImageRef[];
  startImageId?: string;
  endImageId?: string;
  onRemove: (id: string) => void;
  onSelectStart: (id: string) => void;
  onSelectEnd: (id: string) => void;
  onReorder: (newOrder: ImageRef[]) => void;
  onAddImages?: (images: ImageRef[]) => void;
  estateId?: string | null;
  projectId?: string | null;
};

type AddImagesMenuProps = {
  onAddClick: () => void;
  onSelectFromMediaLibrary: () => void;
  disabledMediaLibrary: boolean;
  uploadLabel: string;
  selectFromMediaLibraryLabel: string;
  cancelLabel: string;
  addImagesLabel: string;
};

const AddImagesMenu = ({
  onAddClick,
  onSelectFromMediaLibrary,
  disabledMediaLibrary,
  uploadLabel,
  selectFromMediaLibraryLabel,
  cancelLabel,
  addImagesLabel,
}: AddImagesMenuProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={addImagesLabel}
          className="ml-2 flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-primary-light bg-transparent text-primary-light"
        >
          <Plus className="h-6 w-6 stroke-[2.5]" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="min-w-[180px] bg-white text-xs" align="center" side="bottom">
        <DropdownMenuItem onSelect={onAddClick} className="cursor-pointer">
          {uploadLabel}
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer"
          disabled={disabledMediaLibrary}
          onSelect={() => {
            if (disabledMediaLibrary) return;
            onSelectFromMediaLibrary();
          }}
        >
          {selectFromMediaLibraryLabel}
        </DropdownMenuItem>

        <DropdownMenuItem className="cursor-pointer text-red-500 hover:text-red-500">
          {cancelLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const DROP_ZONE_WIDTH = 32;

function ChosenImagesStrip({
  images,
  startImageId,
  endImageId,
  onRemove,
  onSelectStart,
  onSelectEnd,
  onReorder,
  onAddImages,
  estateId,
  projectId,
}: ChosenImagesStripProps) {
  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [liveMessage, setLiveMessage] = useState('');
  const blobUrlsRef = useRef<Set<string>>(new Set());
  const mediaLibraryModal = useModal();

  useEffect(() => {
    const nextBlobUrls = new Set<string>();

    images.forEach((img) => {
      if (typeof img.url === 'string' && img.url.startsWith('blob:')) {
        nextBlobUrls.add(img.url);
      }
    });

    blobUrlsRef.current.forEach((url) => {
      if (!nextBlobUrls.has(url)) {
        URL.revokeObjectURL(url);
      }
    });

    blobUrlsRef.current = nextBlobUrls;
  }, [images]);

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []);

  const moveImage = (id: string, direction: 'left' | 'right') => {
    const index = images.findIndex((img) => img.id === id);
    if (index === -1) return;

    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= images.length) return;

    const next = [...images];
    const [removed] = next.splice(index, 1);
    next.splice(targetIndex, 0, removed);

    onReorder(next);
  };

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };

  const handleSelectFromMediaLibrary = () => {
    if (!onAddImages) return;
    if (!estateId && !projectId) return;

    mediaLibraryModal.open();
  };

  const handleSaveSelectedMedia = (image: { mediaId?: string | null } | null) => {
    if (!onAddImages) return;
    if (!image?.mediaId) return;

    const url = `${env.API_URL}/images/${image.mediaId}`;
    onAddImages([{ id: image.mediaId, url }]);
  };

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    if (!onAddImages) return;

    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;

    const remainingSlots = IMAGE_TO_VIDEO_MAX_IMAGES - images.length;

    if (remainingSlots <= 0) {
      toast.warning(t('image-to-video-max-images-warning', { value: IMAGE_TO_VIDEO_MAX_IMAGES }));
      event.target.value = '';
      return;
    }

    const selectedFiles = files.slice(0, remainingSlots);

    const newImages: ImageRef[] = selectedFiles.map((file, index) => {
      const url = URL.createObjectURL(file);
      blobUrlsRef.current.add(url);

      return {
        id: `strip-${Date.now()}-${index}`,
        url,
      };
    });

    onAddImages(newImages);

    if (newImages.length > 0) {
      setLiveMessage(
        t('image-strip-announcement-added', {
          count: newImages.length,
          total: images.length + newImages.length,
        })
      );
    }

    if (files.length > remainingSlots) {
      toast.warning(t('image-to-video-max-images-warning', { value: IMAGE_TO_VIDEO_MAX_IMAGES }));
    }

    event.target.value = '';
  };

  const handleRemove = (id: string, index: number) => {
    onRemove(id);
    setLiveMessage(
      t('image-strip-announcement-removed', {
        position: index + 1,
        remaining: Math.max(0, images.length - 1),
      })
    );
  };

  const handleDragStart = (id: string, event: React.DragEvent<HTMLLIElement>) => {
    setDraggedId(id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', id);
  };

  const resetDragState = () => {
    setDraggedId(null);
    setDragOverIndex(null);
  };

  const handleDropAtIndex = (targetIndex: number) => {
    if (!draggedId) return;

    const fromIndex = images.findIndex((img) => img.id === draggedId);
    if (fromIndex === -1) {
      resetDragState();
      return;
    }

    let toIndex = targetIndex;
    if (fromIndex < toIndex) {
      toIndex -= 1;
    }

    if (fromIndex === toIndex) {
      resetDragState();
      return;
    }

    const next = [...images];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    onReorder(next);

    setLiveMessage(
      t('image-strip-announcement-moved', {
        position: toIndex + 1,
        total: images.length,
      })
    );

    resetDragState();
  };

  const handleDragEnd = () => {
    resetDragState();
  };

  const handleDragOverDropZone = (event: React.DragEvent<HTMLLIElement>, index: number) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (!draggedId) return;
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDropOnDropZone = (event: React.DragEvent<HTMLLIElement>, index: number) => {
    event.preventDefault();
    handleDropAtIndex(index);
  };

  const renderDropZone = (index: number) => (
    <li
      key={`drop-zone-${index}`}
      className="relative flex h-24 shrink-0 items-stretch"
      style={{ width: DROP_ZONE_WIDTH }}
      aria-hidden="true"
      onDragOver={(event) => handleDragOverDropZone(event, index)}
      onDrop={(event) => handleDropOnDropZone(event, index)}
    >
      <div
        className={`absolute inset-0 rounded-md transition-colors ${
          draggedId && dragOverIndex === index ? 'bg-primary-light/10' : 'bg-transparent'
        }`}
      />
      <div className="pointer-events-none absolute inset-y-0 left-1/2 flex -translate-x-1/2 items-center">
        <div
          className={`h-full w-1 rounded-full transition-colors ${
            draggedId && dragOverIndex === index ? 'bg-primary-light' : 'bg-transparent'
          }`}
        />
      </div>
    </li>
  );

  const canShowAddButton = Boolean(onAddImages && images.length < IMAGE_TO_VIDEO_MAX_IMAGES);
  const isMediaLibraryDisabled = !estateId && !projectId;

  const addImagesMenu = canShowAddButton ? (
    <AddImagesMenu
      onAddClick={handleAddClick}
      onSelectFromMediaLibrary={handleSelectFromMediaLibrary}
      disabledMediaLibrary={isMediaLibraryDisabled}
      uploadLabel={t('upload')}
      selectFromMediaLibraryLabel={t('selectFromMediaLibrary')}
      cancelLabel={t('cancel')}
      addImagesLabel={t('add-images')}
    />
  ) : null;

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      multiple
      className="hidden"
      onChange={handleFilesSelected}
    />
  );

  const mediaLibrary = estateId ? (
    <MediaLibraryModal
      modal={mediaLibraryModal}
      type="estate"
      estateId={estateId}
      onSaveSelected={handleSaveSelectedMedia}
    />
  ) : projectId ? (
    <MediaLibraryModal
      modal={mediaLibraryModal}
      type="project"
      projectId={projectId}
      onSaveSelected={handleSaveSelectedMedia}
    />
  ) : null;

  if (images.length === 0) {
    return (
      <>
        <div className="flex items-center justify-between px-6">
          <div aria-live="polite" className="sr-only">
            {liveMessage}
          </div>
          <div className="text-sm text-gray-500">{t('image-to-video-no-images-strip')}</div>

          {addImagesMenu}
        </div>
        {hiddenFileInput}
        {mediaLibrary}
      </>
    );
  }

  return (
    <>
      <ul
        className="flex items-start overflow-x-auto px-6"
        aria-label={t('image-strip-list-label')}
      >
        <li aria-live="polite" className="sr-only">
          {liveMessage}
        </li>

        {renderDropZone(0)}

        {images.map((img, index) => {
          const isStart = img.id === startImageId;
          const isEnd = img.id === endImageId;

          return (
            <Fragment key={img.id}>
              <li
                className={`flex shrink-0 flex-col items-center gap-2 ${
                  draggedId === img.id ? 'opacity-60' : ''
                }`}
                draggable
                onDragStart={(event) => handleDragStart(img.id, event)}
                onDragEnd={handleDragEnd}
              >
                <div className="relative h-24 w-32">
                  <img
                    src={img.url}
                    alt={t('image-strip-image-alt', {
                      index: index + 1,
                      total: images.length,
                    })}
                    className="h-full w-full rounded-md border object-cover"
                  />

                  <div className="absolute inset-y-0 left-0 flex flex-col justify-between px-1 py-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      rounded
                      className="h-6 w-6 bg-white/80 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
                      aria-label={t('image-strip-move-left', { index: index + 1 })}
                      disabled={index === 0}
                      onClick={() => moveImage(img.id, 'left')}
                    >
                      {'<'}
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      rounded
                      className="h-6 w-6 bg-white/80 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
                      aria-label={t('image-strip-move-right', { index: index + 1 })}
                      disabled={index === images.length - 1}
                      onClick={() => moveImage(img.id, 'right')}
                    >
                      {'>'}
                    </Button>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    rounded
                    className="absolute right-1 top-1 h-5 w-5 bg-white/80 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
                    aria-label={t('image-strip-remove', { index: index + 1 })}
                    onClick={() => handleRemove(img.id, index)}
                  >
                    ✕
                  </Button>
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-light text-[10px] font-semibold text-white">
                    {index + 1}
                  </span>

                  {isStart && (
                    <button
                      type="button"
                      className="font-semibold text-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
                      onClick={() => onSelectStart(img.id)}
                    >
                      {t('start-image')}
                    </button>
                  )}

                  {images.length > 1 && isEnd && (
                    <button
                      type="button"
                      className="font-semibold text-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
                      onClick={() => onSelectEnd(img.id)}
                    >
                      {t('end-image')}
                    </button>
                  )}
                </div>
              </li>

              {renderDropZone(index + 1)}
            </Fragment>
          );
        })}

        {addImagesMenu ? (
          <li className="ml-2 flex shrink-0 items-center">{addImagesMenu}</li>
        ) : null}
      </ul>
      {hiddenFileInput}
      {mediaLibrary}
    </>
  );
}

export default ChosenImagesStrip;
